import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { WalletIcon } from '@phosphor-icons/react';

import { updateTeamSessionBudget, type Team } from '@/api-models/teams';
import { useSpace } from '@/contexts/space-context';
import { Button } from '@/ui/button/Button';
import { Card } from '@/ui/Card';
import { Input } from '@/ui/Input';

/**
 * The one control on an otherwise read-only page: the ceiling this space puts on
 * every new agent session.
 *
 * It sits with usage rather than in its own settings section because the number
 * only means something next to the spend it is meant to bound — you set a cap
 * while looking at what sessions actually cost. Editing it never touches sessions
 * already running; their cap was stamped on at creation.
 */
export function BudgetCard({ team }: { team: Team }) {
  const { mutateTeams } = useSpace();

  const currentLimit = team.default_session_budget_limit_usd ?? null;
  const [limit, setLimit] = useState(currentLimit === null ? '' : String(currentLimit));
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setLimit(currentLimit === null ? '' : String(currentLimit));
  }, [currentLimit]);

  const parsed = Number(limit.trim());
  const isValid = limit.trim() !== '' && Number.isFinite(parsed) && parsed > 0;
  const dirty = isValid && parsed !== currentLimit;

  async function save(limitUsd: number | null) {
    setIsSaving(true);
    try {
      await updateTeamSessionBudget(team.id, limitUsd);
      await mutateTeams();
      toast.success(limitUsd === null ? 'Session budget removed' : 'Session budget updated');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update the session budget');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Card radius="xl" padding="md" className="flex flex-wrap items-center justify-between gap-4">
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-tile bg-surface-4">
          <WalletIcon size={18} weight="duotone" className="text-fg-secondary" />
        </div>
        <div className="flex min-w-0 flex-col gap-0.5">
          <h2 className="card-heading">Budget</h2>
          <p className="text-xs leading-4.25 text-fg-tertiary">
            {currentLimit === null
              ? 'New agent sessions run uncapped. Set a limit to stop a session once it has spent that much.'
              : 'Spend cap applied to every new agent session. Running sessions and per-session overrides are unaffected.'}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-fg-muted">$</span>
          <Input
            type="number"
            min="0"
            step="0.5"
            inputMode="decimal"
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
            placeholder="No limit"
            aria-label="Default session budget in USD"
            className="w-36 pl-6"
          />
        </div>
        <Button
          type="button"
          variant="primary"
          size={9}
          onClick={() => save(parsed)}
          disabled={!dirty}
          isLoading={isSaving}
        >
          Save
        </Button>
        {currentLimit !== null && (
          <Button type="button" variant="ghost" size={9} disabled={isSaving} onClick={() => save(null)}>
            Remove
          </Button>
        )}
      </div>
    </Card>
  );
}
